import { Theme } from "@mui/material";

/**
 * A resting place for the hover tooltip Mermaid appends to `<body>`.
 *
 * Every flowchart render adds one `div.mermaidTooltip`, `position: absolute`
 * with no `top`/`left` until a node with a title is hovered. Without them it
 * sits at its static position — after the last in-flow element, at the very
 * bottom of the document — and its 2px padding and 1px border make the page
 * 6px taller than the window: a view sized to fill the window scrolled by
 * exactly that much as soon as it showed a diagram. Mermaid sets both inline
 * when it shows the tooltip, so these only apply while it is hidden (it also
 * sets `pointer-events: none` inline, so the parked box takes no clicks).
 */
export const MERMAID_TOOLTIP_BASELINE = {
	".mermaidTooltip": { top: 0, left: 0 },
};

/**
 * Builds the `%%{init}%%` directive that makes a Mermaid diagram follow the MUI
 * theme, so diagrams recolour with light/dark mode instead of keeping Mermaid's
 * own palette.
 *
 * Mermaid honours only the LAST init directive in a source, so a diagram must
 * carry exactly one — pass any extra variables and config through the options
 * rather than emitting a second directive.
 *
 * Callers should also remount on theme change (`key={theme.palette.mode}`);
 * Mermaid renders to SVG once and will not repaint on its own.
 */
export interface MermaidInitOptions {
	/** Extra theme variables, merged over the theme-derived defaults. */
	themeVariables?: Record<string, string>;
	/** Extra top-level config, e.g. `{ flowchart: { wrappingWidth: 300 } }`. */
	config?: Record<string, unknown>;
}

export function mermaidInitDirective(
	theme: Theme,
	options: MermaidInitOptions = {}
): string {
	const { mermaidGraph } = theme;
	const directive = {
		theme: "base",
		themeVariables: {
			primaryColor: mermaidGraph.rectFillColor,
			primaryBorderColor: mermaidGraph.rectBorderColor,
			primaryTextColor: mermaidGraph.textColor,
			secondaryTextColor: mermaidGraph.textColor,
			tertiaryTextColor: mermaidGraph.textColor,
			textColor: mermaidGraph.textColor,
			nodeTextColor: mermaidGraph.textColor,
			labelTextColor: mermaidGraph.textColor,
			lineColor: mermaidGraph.linkColor,
			...options.themeVariables,
		},
		...options.config,
	};
	return `%%{init: ${JSON.stringify(directive)}}%%\n`;
}
